import { authedFetch } from '@/lib/authed-fetch';
import type {
  EventCheckInEligibility,
  EventCheckInPayment,
} from '@sixam/shared/event-checkin-core';
import type { Database } from '@sixam/shared/database.types';

export type EventCheckIn = Database['public']['Tables']['event_checkins']['Row'];

/** Event-specific context about the runner behind the scanned QR. */
export type ScannedAttendee = Readonly<{
  alreadyCheckedIn: boolean;
  checkedInAt: string | null;
  payment: EventCheckInPayment;
  registeredAt: string | null;
}>;

export type EventCheckInEligibilityResponse = Readonly<{
  eligibility: EventCheckInEligibility;
}>;

export type CheckInReviewReason = 'over_capacity' | 'unpaid_walk_up' | 'walk_up';

export type EventCheckInResponse = Readonly<{
  attendee?: ScannedAttendee;
  checkIn: EventCheckIn;
  member?: { name: string };
  message: string;
  /** The runner is in regardless; this only means a human should look later. */
  needsReview?: boolean;
  reviewReason?: CheckInReviewReason | null;
  status: 'checked_in' | 'duplicate';
}>;

/**
 * A QR the server would not accept — forged, expired, or for another event. The
 * station shows the operator a line they can act on instead of a token error.
 */
export class InvalidMemberQrError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidMemberQrError';
  }
}

async function parseApiResponse<T>(response: Response, fallbackMessage: string): Promise<T> {
  const payload = (await response.json().catch(() => null)) as
    ({ error?: string; reason?: string } & T) | null;

  if (!response.ok) {
    if (payload?.reason === 'invalid_qr') {
      throw new InvalidMemberQrError(payload.error ?? fallbackMessage);
    }
    throw new Error(payload?.error ?? fallbackMessage);
  }

  return payload as T;
}

export type LiveCheckInEvent = Readonly<{
  checkin_mode: string;
  ends_at: string | null;
  id: string;
  starts_at: string;
  title: string;
}>;

/** Which side of the scan the caller is on for the live event. */
export type LiveCheckInRole = 'operator' | 'attendee';

export type LiveCheckIn = Readonly<{
  event: LiveCheckInEvent;
  role: LiveCheckInRole;
}>;

/**
 * The event this user's app should open onto and why — the camera for an
 * operator, their own code for someone about to run it. Null when nothing is
 * live for them.
 */
export async function fetchLiveCheckIn(): Promise<LiveCheckIn | null> {
  const response = await authedFetch('/api/check-in/live');
  const payload = await parseApiResponse<{
    event: LiveCheckInEvent | null;
    role: LiveCheckInRole | null;
  }>(response, 'Could not load live check-in.');

  if (!payload.event || !payload.role) {
    return null;
  }
  return { event: payload.event, role: payload.role };
}

export type MyLiveCheckIn = Readonly<{
  checkedIn: boolean;
  event: { id: string; title: string } | null;
}>;

/**
 * Whether the caller has just been checked in to something under way. Needed
 * because a walk-up has no event of their own to ask about — they only have
 * their code.
 */
export async function fetchMyLiveCheckIn(): Promise<MyLiveCheckIn> {
  const response = await authedFetch('/api/check-in/mine');
  return parseApiResponse<MyLiveCheckIn>(response, 'Could not load your check-in.');
}

export async function fetchEventCheckInEligibility(
  eventId: string
): Promise<EventCheckInEligibilityResponse> {
  const response = await authedFetch(`/api/events/${eventId}/checkins`);
  return parseApiResponse<EventCheckInEligibilityResponse>(
    response,
    'Could not load check-in settings.'
  );
}

/**
 * Checks the scanned runner straight in. There is deliberately no confirmation
 * round trip: at the start line a dialog between the camera and the queue costs
 * more than any mis-scan it would prevent, and a wrong check-in is undone from
 * the roster.
 */
export async function checkInEventMember(
  eventId: string,
  qrToken: string
): Promise<EventCheckInResponse> {
  const response = await authedFetch(`/api/events/${eventId}/checkins`, {
    body: JSON.stringify({ qrToken }),
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  });
  return parseApiResponse<EventCheckInResponse>(response, 'Could not check in member.');
}

/** Remove a runner's check-in for an event. */
export async function checkOutEventMember(
  eventId: string,
  qrToken: string
): Promise<{ message: string; status: 'checked_out' }> {
  const response = await authedFetch(`/api/events/${eventId}/checkins`, {
    body: JSON.stringify({ action: 'check_out', qrToken }),
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  });
  return parseApiResponse(response, 'Could not check out member.');
}
